import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

import WeatherApi from '@/api/config/weatherApi'

import {
  getColumnDetails,
  WeatherReportFields,
} from '@store/slices/DataComparison/types'
import {
  ChartData,
  DataComparisonState,
  SensorDataColumns,
  SensorHistoricDataRequestParams,
  Sensors,
  SensorTypes,
  TimeGrouping,
} from '../types/DataComparisonType'

import { staticConvertToMetric } from '@/hooks/useUnits'
import { formatNumber } from '@/ultils/number'
import { AssetData } from './DataComparison/types'
import { fitBucketsToWindow } from '@/components/dynamic-dashboard/widget-components/helper'

export const seedWeatherStationDataColumns: SensorDataColumns[] =
  Object.values(WeatherReportFields)

export const seedSensorData: Sensors[] = [
  {
    id: 8,
    typeId: 1,
    name: 'West Torrens',
    dataColumns: seedWeatherStationDataColumns,
  },
  {
    id: 2,
    typeId: 1,
    name: 'SB3',
    dataColumns: seedWeatherStationDataColumns,
  },
]

export const seedSensorTypes: SensorTypes[] = [
  {
    id: 1,
    name: 'Weather Station',
  },
]

const initialState: DataComparisonState = {
  sensors: {
    isLoading: false,
    data: [],
  },
  sensorTypes: {
    isLoading: false,
    data: [],
  },
  chartDetails: {
    isLoading: false,
    data: [],
  },
  currentCSVData: undefined,
  rightSidebarDetails: {
    variantName: '',
    sensorStateData: [],
    allSensorStateData: [],
    checkedSensorParameter: [],
    openSensor: [],
    sensorType: [],
    inputValue: '',
  },
  groupByFields: [],
  groupByMode: 'all',
  timeGrouping: TimeGrouping.FIFTEEN_MINS,
  snapshotBeforePanelOpen: null,
}

export const sensorTypeOrder = [
  'Weather Station',
  'Barometer Sensor',
  'PAR Sensor',
  'Indoor Climate Sensor',
  'Soil Moisture Sensor',
  'pH Sensor',
]

const GROUPING_INTERVAL_MS: Partial<Record<TimeGrouping, number>> = {
  [TimeGrouping.ONE_MIN]: 1 * 60 * 1000,
  [TimeGrouping.FIVE_MINS]: 5 * 60 * 1000,
  [TimeGrouping.FIFTEEN_MINS]: 15 * 60 * 1000,
  [TimeGrouping.ONE_HOUR]: 60 * 60 * 1000,
  [TimeGrouping.SIX_HOURS]: 6 * 60 * 60 * 1000,
  [TimeGrouping.TWELVE_HOURS]: 12 * 60 * 60 * 1000,
  [TimeGrouping.ONE_DAY]: 24 * 60 * 60 * 1000,
  [TimeGrouping.TWO_DAYS]: 48 * 60 * 60 * 1000,
}

function groupDataPoints(
  data: { timestamp: string; value: number; [key: string]: any }[],
  intervalMs: number,
  isSum: boolean,
  timezoneOffsetMs: number,
  groupByFields: string[] = [],
): { timestamp: string; value: number; [key: string]: any }[] {
  const grouped: Record<
    string,
    { values: number[]; extra: Record<string, any> }
  > = {}

  const validData = data.filter((d) => d.value !== null)

  validData.forEach((point) => {
    const t = new Date(point.timestamp).getTime()
    const adjusted = t + timezoneOffsetMs
    const bucket =
      Math.floor(adjusted / intervalMs) * intervalMs - timezoneOffsetMs

    const groupSuffix = groupByFields
      .map((f) => `${f}:${point[f] ?? 'N/A'}`)
      .join('|')
    const key = `${bucket}__${groupSuffix}`

    if (!grouped[key]) {
      const extra: Record<string, any> = {}
      groupByFields.forEach((f) => {
        if (point[f] != null) extra[f] = point[f]
      })
      grouped[key] = { values: [], extra }
    }
    grouped[key].values.push(point.value)
  })

  return Object.entries(grouped)
    .map(([key, { values, extra }]) => {
      const bucketMs = Number(key.split('__')[0])
      const value = isSum
        ? values.reduce((s, v) => s + v, 0)
        : values.reduce((s, v) => s + v, 0) / values.length
      return { timestamp: new Date(bucketMs).toISOString(), value, ...extra }
    })
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    )
}

export const fetchSensorHistoricData = createAsyncThunk(
  'weatherDashboard/fetchSensorHistoricData',
  async (
    params: SensorHistoricDataRequestParams,
    { rejectWithValue, getState },
  ) => {
    try {
      const state: any = getState()
      const timeGrouping =
        params.timeGrouping ||
        (state.savedComparison?.timeGrouping as TimeGrouping | '') ||
        ''
      const groupByFields: string[] =
        state.dataComparisonV2?.groupByFields ??
        state.savedComparison?.groupByFields ??
        []
      const groupByFieldKeys = groupByFields
      const tz: string =
        state.srcInterface?.currentInterface?.timeZoneOriginId || 'UTC'
      const intervalMs = timeGrouping
        ? GROUPING_INTERVAL_MS[timeGrouping]
        : undefined
      const timezoneOffsetMs = intervalMs
        ? dayjs().tz(tz).utcOffset() * 60 * 1000
        : 0
      const allSensorHistoricData: ChartData[] = []
      const windowStartMs = new Date(params.startDate).getTime()
      const windowEndMs = new Date(params.endDate).getTime()

      for (const sensorRequest of params.sensorList) {
        const extraNames = groupByFieldKeys.filter(
          (f) => !sensorRequest.dataColumns.includes(f),
        )
        const { data } = await WeatherApi.getWeatherReportV2({
          AssetId: sensorRequest.sensorId,
          TimeBucket: 1,
          FromTime: params.startDate,
          ToTime: params.endDate,
          Names: [...sensorRequest.dataColumns, ...extraNames],
          PageSize: 10000000,
          PageIndex: 1,
        })

        const chartData = convertToChartData(
          data ?? [],
          sensorRequest.sensorId,
          sensorRequest.sensorName,
          sensorRequest.sensorTypeId,
          sensorRequest.metadata,
          groupByFieldKeys,
        )
        if (intervalMs) {
          chartData.forEach((series) => {
            series.data = groupDataPoints(
              series.data,
              intervalMs,
              series.chartType === 'barChart',
              timezoneOffsetMs,
              groupByFieldKeys,
            )
            series.data = fitBucketsToWindow(
              series.data,
              windowStartMs,
              windowEndMs,
              series.chartType !== 'barChart',
            )
          })
        }

        allSensorHistoricData.push(...chartData)
      }
      return allSensorHistoricData
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

function convertToChartData(
  rawData: any[],
  sensorId: number,
  sensorName: string,
  sensorTypeId: number,
  metadata: any,
  groupByFields: string[] = [],
): ChartData[] {
  const nullableKeys = [
    WeatherReportFields.Evapotranspiration.columnName.toLowerCase(),
    WeatherReportFields.RainFall.columnName.toLowerCase(),
  ]

  const chartMap: Record<string, ChartData> = {}

  rawData.forEach((item) => {
    const timestamp = item.time as string

    Object.entries(item).forEach(([key, value]) => {
      if (key === 'time') return
      if (groupByFields.includes(key)) return

      const columnDetail = getColumnDetails(key)
      if (!chartMap[key]) {
        chartMap[key] = {
          sensorId,
          sensorName,
          chartAxisName: columnDetail?.chartAxisName ?? '',
          columnName: columnDetail?.displayName ?? key,
          unitMetric: columnDetail?.metricUnit ?? metadata?.metric ?? '',
          unitImperial: columnDetail?.imperialUnit ?? metadata?.imperial ?? '',
          chartType: columnDetail?.chartType ?? metadata?.chartType ?? '',
          isFixed: columnDetail?.isFixed ?? 2,
          selectionKey: `${sensorTypeId}+${sensorId}+${sensorName}+${key}`,
          data: [],
        }
      }

      if (value || value === 0 || nullableKeys.includes(key)) {
        const valueNumber = typeof value === 'number' ? value : Number(value)
        const metricValue = Number(Math.round(valueNumber * 100) / 100)
        // Attach group-by field values to the data point so the chart can split/average
        const dataPoint: any = { timestamp, value: metricValue }
        groupByFields.forEach((f) => {
          if (item[f] !== undefined) dataPoint[f] = item[f]
        })
        chartMap[key].data.push(dataPoint)
      }
    })
  })

  return Object.values(chartMap)
}

export const fetchSensorData = createAsyncThunk(
  'dataComparison/fetchSensorData',
  async (interfaceId: string, { rejectWithValue }) => {
    try {
      return seedSensorData as Sensors[]
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchSensorTypes = createAsyncThunk(
  'dataComparison/fetchSensorTypes',
  async (interfaceId: string, { rejectWithValue }) => {
    try {
      return seedSensorTypes as SensorTypes[]
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const savedComparisonSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setupWeatherStationSavedComparison: (
      state,
      action: PayloadAction<{
        assetData: AssetData[]
        sensorTypes: SensorTypes[]
      }>,
    ) => {
      const { assetData, sensorTypes } = action.payload
      const extractedSensorData = assetData
        .map((item) => {
          const typeName = item.metadata?.type || 'Other'
          const matchedType = sensorTypes?.find((st) => st.name === typeName)
          if (!matchedType?.id) return null

          return {
            id: item.id,
            typeId: matchedType.id,
            name: item.deviceName,
            dataColumns: seedWeatherStationDataColumns,
            metadata: item.metadata,
            telemetryAssetId: item.telemetryAssetId,
          }
        })
        .filter((item): item is NonNullable<typeof item> => item !== null)
      state.sensors.data = extractedSensorData
    },
    setRightSidePanelSavedComparison: (state, action) => {
      const sensors = [...action.payload.sensorType]
      sensors.sort(
        (a, b) =>
          sensorTypeOrder.indexOf(a.name) - sensorTypeOrder.indexOf(b.name),
      )
      state.rightSidebarDetails = action.payload
      state.rightSidebarDetails.sensorType = sensors
    },
    patchSensorSelectionSavedComparison: (
      state,
      action: PayloadAction<{
        checkedSensorParameter: any
        openSensor: any
        sensorType?: any[]
      }>,
    ) => {
      // Merges only the selection fields — never touches sensorStateData / allSensorStateData
      const next = { ...state.rightSidebarDetails } as any
      next.checkedSensorParameter = action.payload.checkedSensorParameter
      next.openSensor = action.payload.openSensor
      if (action.payload.sensorType !== undefined) {
        next.sensorType = action.payload.sensorType
      }
      state.rightSidebarDetails = next
    },
    setCurrentCSVDataSavedComparison: (state, action) => {
      state.currentCSVData = action.payload
    },
    setTimeGroupingSavedComparison: (
      state,
      action: PayloadAction<{
        timeGrouping: TimeGrouping | ''
      }>,
    ) => {
      state.timeGrouping = action.payload.timeGrouping
    },
    setChartLoadingSavedComparison: (state, action: PayloadAction<boolean>) => {
      state.chartDetails.isLoading = action.payload
      state.chartDetails.data = []
    },
    setGroupByFieldsSavedComparison: (
      state,
      action: PayloadAction<{
        groupByFields: string[]
        groupByMode?: 'all' | 'average'
      }>,
    ) => {
      state.groupByFields = action.payload.groupByFields
      if (action.payload.groupByMode !== undefined) {
        state.groupByMode = action.payload.groupByMode
      }
    },
    snapshotSensorSelectionSavedComparison: (state) => {
      state.snapshotBeforePanelOpen = { ...state.rightSidebarDetails.checkedSensorParameter }
    },
    revertSensorSelectionSavedComparison: (state) => {
      if (!state.snapshotBeforePanelOpen) return
      state.rightSidebarDetails.checkedSensorParameter = state.snapshotBeforePanelOpen
      state.snapshotBeforePanelOpen = null
    },
    clearSensorSelectionSnapshot: (state) => {
      state.snapshotBeforePanelOpen = null
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchSensorData
      .addCase(fetchSensorData.pending, (state) => {
        state.sensors.isLoading = true
      })
      .addCase(fetchSensorData.fulfilled, (state, action) => {
        state.sensors.isLoading = false
        state.sensors.data = action.payload
      })
      .addCase(fetchSensorData.rejected, (state) => {
        state.sensors.isLoading = false
      })
      // fetchSensorTypes
      .addCase(fetchSensorTypes.pending, (state) => {
        state.sensorTypes.isLoading = true
      })
      .addCase(fetchSensorTypes.fulfilled, (state, action) => {
        state.sensors.isLoading = false
        state.sensorTypes.data = action.payload
      })
      .addCase(fetchSensorTypes.rejected, (state) => {
        state.sensors.isLoading = false
      })
      // fetchSensorHistoricData
      .addCase(fetchSensorHistoricData.pending, (state) => {
        state.chartDetails.isLoading = true
      })
      .addCase(fetchSensorHistoricData.fulfilled, (state, action) => {
        state.chartDetails.isLoading = false
        state.chartDetails.data = action.payload
      })
      .addCase(fetchSensorHistoricData.rejected, (state) => {
        state.chartDetails.isLoading = false
      })
  },
})
// Action creators are generated for each case reducer function
export const {
  setupWeatherStationSavedComparison,
  setRightSidePanelSavedComparison,
  patchSensorSelectionSavedComparison,
  setCurrentCSVDataSavedComparison,
  setTimeGroupingSavedComparison,
  setChartLoadingSavedComparison,
  setGroupByFieldsSavedComparison,
  snapshotSensorSelectionSavedComparison,
  revertSensorSelectionSavedComparison,
  clearSensorSelectionSnapshot,
} = savedComparisonSlice.actions

export default savedComparisonSlice.reducer
